import {
  Stack,
  CommandBar,
  DetailsList,
  IconButton,
  SelectionMode,
  Panel,
  PanelType,
  TextField,
} from '@fluentui/react';
import {
  Button,
  Checkbox,
  Field,
  FluentProvider,
  Input,
  makeStyles,
  webLightTheme,
} from '@fluentui/react-components';
import { IFieldsTab } from '../../services/metadataService';
import { useBoolean } from '@fluentui/react-hooks';
import { useState } from 'react';

export interface IFieldsTabsEditor {
  disabled: boolean;
  fieldsTab?: IFieldsTab[];
  onChange: (newValue: IFieldsTab[]) => void;
}

const useStyles = makeStyles({
  panelBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  actionsRow: {
    display: 'flex',
    gap: '8px',
    marginTop: '8px',
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    minHeight: '32px',
  },
});

const FieldsTabsEditor = (props: IFieldsTabsEditor) => {
  const classes = useStyles();
  const [
    isTabEditorPanelOpen,
    { setTrue: showTabEditorPanel, setFalse: hideTabEditorPanel },
  ] = useBoolean(false);
  const [fieldsTab, setFieldsTab] = useState<IFieldsTab[]>(
    props.fieldsTab ?? []
  );
  const [currentTab, setCurrentTab] = useState<IFieldsTab>();
  const [selectedRow, setSelectedRow] = useState<number>(0);

  return (
    <>
      <Stack>
        <CommandBar
          items={[
            {
              key: 'AddTab',
              text: 'Add',
              disabled: props.disabled,
              iconProps: { iconName: 'Add' },
              onClick: () => {
                setSelectedRow(-1);
                setCurrentTab({
                  tabName: '',
                  order: fieldsTab.length + 1,
                  caption: '',
                  hidden: false,
                });
                showTabEditorPanel();
              },
            },
          ]}
        />
        <DetailsList
          columns={[
            {
              key: 'tabName',
              name: 'tabName',
              fieldName: 'tabName',
              minWidth: 100,
            },
            {
              key: 'caption',
              name: 'Caption',
              fieldName: 'caption',
              minWidth: 100,
            },
            {
              key: 'hidden',
              name: 'Hidden',
              fieldName: 'hidden',
              minWidth: 70,
              maxWidth: 90,
              onRender: (item: IFieldsTab) => (item.hidden ? 'Yes' : 'No'),
            },
            {
              key: 'action',
              name: 'Action',
              minWidth: 85,
              onRender: (item: any, index?: number) => (
                <Stack horizontal>
                  <IconButton
                    iconProps={{ iconName: 'Edit' }}
                    onClick={() => {
                      setSelectedRow(index ?? 0);
                      setCurrentTab(item);
                      showTabEditorPanel();
                    }}
                  />
                  <IconButton
                    iconProps={{ iconName: 'Delete' }}
                    onClick={() => {
                      const newTabs = fieldsTab.filter((_, i) => i !== index);
                      setFieldsTab(newTabs);
                      props.onChange(newTabs);
                    }}
                  />
                </Stack>
              ),
            },
          ]}
          items={
            fieldsTab?.map((v, index) => ({
              ...v,
              index: index,
            })) ?? []
          }
          selectionMode={SelectionMode.none}
        />
      </Stack>
      <Panel
        isOpen={isTabEditorPanelOpen}
        onDismiss={hideTabEditorPanel}
        closeButtonAriaLabel="Close"
        type={PanelType.smallFixedFar}
        headerText={'Field Tab'}
      >
        <FluentProvider theme={webLightTheme}>
          <div className={classes.panelBody}>
            <Field label="Enter the key:">
              <Input
                value={currentTab?.tabName ?? ''}
                onChange={(_event, data) => {
                  setCurrentTab({
                    tabName: data.value,
                    order: currentTab?.order ?? 0,
                    caption: currentTab?.caption ?? '',
                    hidden: currentTab?.hidden ?? false,
                  });
                }}
              />
            </Field>
            <Field label="Enter the caption:">
              <Input
                value={currentTab?.caption ?? ''}
                onChange={(_event, data) => {
                  setCurrentTab({
                    tabName: currentTab?.tabName ?? '',
                    order: currentTab?.order ?? 0,
                    caption: data.value,
                    hidden: currentTab?.hidden ?? false,
                  });
                }}
              />
            </Field>
            <div className={classes.checkboxRow}>
              <Checkbox
                label="Hidden"
                checked={!!currentTab?.hidden}
                onChange={(_event, data) => {
                  setCurrentTab({
                    tabName: currentTab?.tabName ?? '',
                    order: currentTab?.order ?? 0,
                    caption: currentTab?.caption ?? '',
                    hidden: !!data.checked,
                  });
                }}
              />
            </div>
            <div className={classes.actionsRow}>
              <Button
                appearance="primary"
                onClick={() => {
                  const newTab = {
                    tabName: currentTab?.tabName ?? '',
                    order:
                      selectedRow < 0
                        ? fieldsTab.length + 1
                        : currentTab?.order ?? selectedRow + 1,
                    caption: currentTab?.caption ?? '',
                    hidden: currentTab?.hidden ?? false,
                  };
                  const newTabs = [...(fieldsTab ?? [])];
                  if (selectedRow < 0) {
                    newTabs.push(newTab);
                  } else {
                    newTabs.splice(selectedRow, 1, newTab);
                  }
                  setFieldsTab(newTabs);
                  props.onChange(newTabs);
                  hideTabEditorPanel();
                }}
              >
                Save
              </Button>
              <Button appearance="secondary" onClick={hideTabEditorPanel}>
                Cancel
              </Button>
            </div>
          </div>
        </FluentProvider>
      </Panel>
    </>
  );
};

export default FieldsTabsEditor;
